import React, { useEffect, useState } from "react";
import DoctorHeader from "./DoctorHeader";
import DoctorFooter from "./DoctorFooter";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import {
  Menu,
  Users,
  Wallet,
  Bell,
  Clock3,
  ArrowRight,
  Activity,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDoctorUnreadMessages } from "../../hooks/useDoctorUnreadMessages";

const DoctorHome = () => {
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [wallet, setWallet] = useState({});
  const [patients, setPatients] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const navigate = useNavigate();

  const doctor = JSON.parse(localStorage.getItem("doctor") || "{}");
  const { unreadMessages, unreadPatients } = useDoctorUnreadMessages(
    doctor?._id
  );

  const getNotifications = async () => {
    await axios
      .post(
        process.env.REACT_APP_BACKEND_URL + `/doctor/notifications`,
        {
          doctorId: doctor?._id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      )
      .then((res) => {
        console.log(res.data);
        setNotifications(res.data.notifications || []);
      })
      .catch((err) => {
        console.error("Failed to fetch notifications", err);
      });
  };

  const getWalletBalance = async () => {
    await axios
      .post(process.env.REACT_APP_BACKEND_URL + `/doctor-wallet`, {
        doctorId: doctor?._id,
      })
      .then((res) => {
        console.log(res.data);
        setWallet({ balance: res.data.wallet });
      })
      .catch((err) => {
        console.error("Failed to fetch wallet balance", err);
      });
  };

  const fetchPatients = async () => {
    const doctorId = JSON.parse(localStorage.getItem("doctor"))._id;

    const res = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "/clients/by-doctor",
      {
        doctorId,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || null}`,
        },
      }
    );

    if (res.data.success) {
      setPatients(res.data.clients || []);
    }
  };

  useEffect(() => {
    getWalletBalance();
    fetchPatients();
    getNotifications();
  }, []);

  const totalPatients = patients?.length || 0;
  const assignedPatients = patients.filter((p) => p.assignedDoctor).length;
  const pendingPatients = patients.filter((p) => !p.assignedDoctor).length;
  const walletBalance = wallet?.balance || 0;
  const unreadNotificationsText =
    unreadMessages === 1
      ? "Doctor, you have 1 unread notification."
      : `Doctor, you have ${unreadMessages} unread notifications.`;

  const stats = [
    {
      title: "Patients",
      value: totalPatients,
      note: `${assignedPatients} assigned`,
      icon: Users,
      onClick: () => navigate("/dashboard/patients"),
    },
    {
      title: "Pending",
      value: pendingPatients,
      note: "Awaiting assignment",
      icon: Clock3,
      onClick: () => navigate("/dashboard/patients"),
    },
    {
      title: "Wallet",
      value: `₦${walletBalance.toLocaleString()}`,
      note: "Current balance",
      icon: Wallet,
      onClick: () => {},
    },
    {
      title: "Notifications",
      value: unreadMessages,
      note:
        unreadPatients === 1
          ? "1 patient waiting"
          : `${unreadPatients} patients waiting`,
      icon: Bell,
      onClick: () => navigate("/dashboard/patients"),
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      {/* Mobile Sidebar Toggle */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
        >
          <Menu size={18} />
          Menu
        </button>

        <p className="text-sm font-medium text-slate-500">Dashboard</p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden md:flex">
          <DoctorSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>

        {/* Mobile Sidebar Drawer */}
        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        {/* Main panel */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Page intro */}
            <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                  Welcome, Doctor
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                  A quick view of your patients, wallet, and recent activity.
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/dashboard/patients")}
                className="inline-flex w-fit items-center gap-2 rounded-xl bg-[#075E54] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#064b43]"
              >
                View patients
                <ArrowRight size={16} />
              </button>
            </div>

            <button
              type="button"
              onClick={() => navigate("/dashboard/patients")}
              className={`mb-6 flex w-full items-center justify-between gap-4 rounded-2xl border px-5 py-4 text-left transition hover:shadow-sm ${
                unreadMessages > 0
                  ? "border-amber-200 bg-amber-50"
                  : "border-emerald-200 bg-emerald-50"
              }`}
            >
              <div className="min-w-0">
                <p
                  className={`text-sm font-semibold ${
                    unreadMessages > 0 ? "text-amber-900" : "text-emerald-900"
                  }`}
                >
                  {unreadNotificationsText}
                </p>

                <p
                  className={`mt-1 text-sm ${
                    unreadMessages > 0 ? "text-amber-700" : "text-emerald-700"
                  }`}
                >
                  {unreadMessages > 0
                    ? "Open Patients to reply to messages that need attention."
                    : "No unread patient messages right now."}
                </p>
              </div>

              <Bell
                size={20}
                className={
                  unreadMessages > 0 ? "text-amber-700" : "text-emerald-700"
                }
              />
            </button>

            {/* Stats */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {stats.map((item) => {
                const Icon = item.icon;

                return (
                  <button
                    key={item.title}
                    type="button"
                    onClick={item.onClick}
                    className="rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:border-slate-300 hover:shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-slate-500">
                          {item.title}
                        </p>

                        <p className="mt-3 text-2xl font-semibold tracking-[-0.03em] text-slate-950">
                          {item.value}
                        </p>

                        <p className="mt-2 text-xs text-slate-400">
                          {item.note}
                        </p>
                      </div>

                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                        <Icon size={18} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Main content grid */}
            <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
              {/* Recent notifications */}
              <section className="rounded-2xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                  <div>
                    <h3 className="text-base font-semibold text-slate-950">
                      Recent notifications
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Latest patient and system updates.
                    </p>
                  </div>

                  <Bell size={18} className="text-slate-400" />
                </div>

                <div className="divide-y divide-slate-100">
                  {notifications.slice(0, 5).map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left transition hover:bg-slate-50"
                      onClick={() => {
                        if (c.type === "patient_message") {
                          navigate(`/dashboard/patient/${c.client_id}`);
                        }
                      }}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {c.name || "Notification"}
                        </p>

                        <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-500">
                          {c.message}
                        </p>
                      </div>

                      {c.timeAgo && (
                        <span className="shrink-0 text-xs text-slate-400">
                          {c.timeAgo}
                        </span>
                      )}
                    </button>
                  ))}

                  {!notifications.length && (
                    <div className="px-5 py-10 text-center">
                      <p className="text-sm font-medium text-slate-500">
                        No notifications yet.
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Summary */}
              <section className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                    <Activity size={18} />
                  </div>

                  <div>
                    <h3 className="text-base font-semibold text-slate-950">
                      Today
                    </h3>
                    <p className="text-sm text-slate-500">Simple overview</p>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <span className="text-sm text-slate-500">
                      Total patients
                    </span>
                    <span className="text-sm font-semibold text-slate-950">
                      {totalPatients}
                    </span>
                  </div>

                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <span className="text-sm text-slate-500">
                      Assigned patients
                    </span>
                    <span className="text-sm font-semibold text-slate-950">
                      {assignedPatients}
                    </span>
                  </div>

                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <span className="text-sm text-slate-500">
                      Pending patients
                    </span>
                    <span className="text-sm font-semibold text-slate-950">
                      {pendingPatients}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">
                      Wallet balance
                    </span>
                    <span className="text-sm font-semibold text-slate-950">
                      ₦{walletBalance.toLocaleString()}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/dashboard/patients")}
                  className="mt-6 flex h-11 w-full items-center justify-center rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Open patient list
                </button>
              </section>
            </div>
          </div>
        </main>
      </div>

    </div>
  );
};

export default DoctorHome;
